"use client";

import type { DragEvent } from "react";
import { useState } from "react";
import {
  CopyButton,
  ErrorNotice,
  Notice,
  SelectField,
  StatCard,
  StatGrid,
  TextField,
} from "@/components/tool-kit/primitives";
import {
  BUTTON_SECONDARY,
  BUTTON_SMALL,
  HINT,
  INPUT,
  LABEL,
  TEXTAREA,
  WIDGET_H2,
} from "@/components/tool-kit/styles";
import {
  EXAMPLE_PNG_BASE64,
  type ImageStrings,
} from "@/components/tools/image-to-base64";
import { useToolLocale, useToolNumber } from "@/components/tools/tool-ui";
import { t } from "@/lib/i18n/interpolate";
import { downloadText } from "@/lib/tool-kit/download";
import {
  type LoadError,
  type LoadedImage,
  loadImageFile,
  MAX_BYTES,
  measureImage,
} from "@/lib/tool-kit/latex-dev/image-browser";
import {
  buildSnippet,
  type SnippetFormat,
  sizeStats,
} from "@/lib/tool-kit/latex-dev/image-data";

const SHOWN_CHARS = 20_000;
const LARGE_URI = 100_000;
const FORMATS: readonly SnippetFormat[] = [
  "dataUri",
  "base64",
  "html",
  "css",
  "markdown",
  "svgText",
];

type State =
  | { phase: "idle" }
  | { phase: "reading" }
  | { phase: "error"; error: LoadError; mime: string; bytes: number }
  | { phase: "done"; image: LoadedImage };

/** "12 byte", "3.4 kB", "1.2 MB", in the reader's language. */
export function formatSize(bytes: number, locale: string): string {
  let unit = "byte";
  let value = bytes;
  if (bytes >= 1_048_576) {
    unit = "megabyte";
    value = bytes / 1_048_576;
  } else if (bytes >= 1024) {
    unit = "kilobyte";
    value = bytes / 1024;
  }
  return new Intl.NumberFormat(locale, {
    style: "unit",
    unit,
    unitDisplay: "short",
    maximumFractionDigits: unit === "byte" ? 0 : 1,
  }).format(value);
}

export function ImageEncodePanel({ strings }: { strings: ImageStrings }) {
  const locale = useToolLocale();
  const fmt = useToolNumber();
  const [state, setState] = useState<State>({ phase: "idle" });
  const [format, setFormat] = useState<SnippetFormat>("dataUri");
  const [alt, setAlt] = useState("");

  async function take(file: File | undefined) {
    if (!file) {
      return;
    }
    setState({ phase: "reading" });
    const result = await loadImageFile(file);
    setState(
      result.ok
        ? { phase: "done", image: result.image }
        : {
            phase: "error",
            error: result.error,
            mime: result.mime,
            bytes: result.bytes,
          }
    );
  }

  async function loadExample() {
    setState({ phase: "reading" });
    const size = await measureImage(
      `data:image/png;base64,${EXAMPLE_PNG_BASE64}`
    );
    setState({
      phase: "done",
      image: {
        name: "example.png",
        mime: "image/png",
        bytes: 140,
        base64: EXAMPLE_PNG_BASE64,
        svgText: "",
        width: size?.width ?? 16,
        height: size?.height ?? 16,
      },
    });
  }

  function onDrop(event: DragEvent<HTMLDivElement>) {
    event.preventDefault();
    take(event.dataTransfer.files[0]);
  }

  return (
    <div className="space-y-6">
      {/* biome-ignore lint/a11y/noStaticElementInteractions: drag and drop is an extra way to hand over a file; the file input inside is the accessible control */}
      {/* biome-ignore lint/a11y/noNoninteractiveElementInteractions: same drop zone */}
      <div
        className="space-y-3 rounded-lg border border-border border-dashed bg-card p-4"
        onDragOver={(event) => event.preventDefault()}
        onDrop={onDrop}
      >
        <label className={LABEL} htmlFor="itb-file">
          {strings.chooseLabel}
        </label>
        <input
          accept="image/*,.svg,.avif,.webp"
          className={`${INPUT} w-auto`}
          id="itb-file"
          onChange={(event) => {
            take(event.target.files?.[0]);
            event.target.value = "";
          }}
          type="file"
        />
        <p className={HINT}>{strings.dropHint}</p>
        <div className="flex flex-wrap gap-3">
          <button className={BUTTON_SMALL} onClick={loadExample} type="button">
            {strings.loadExample}
          </button>
          <button
            className={BUTTON_SMALL}
            onClick={() => setState({ phase: "idle" })}
            type="button"
          >
            {strings.clear}
          </button>
        </div>
      </div>
      <div aria-live="polite" className="space-y-4">
        {state.phase === "idle" ? <Notice>{strings.emptyEncode}</Notice> : null}
        {state.phase === "reading" ? <Notice>{strings.reading}</Notice> : null}
        {state.phase === "error" ? (
          <ErrorNotice>
            {t(strings.encodeErrors[state.error], {
              size: formatSize(state.bytes, locale),
              limit: formatSize(MAX_BYTES, locale),
              type: state.mime || strings.none,
            })}
          </ErrorNotice>
        ) : null}
        {state.phase === "done" ? (
          <Done
            alt={alt}
            fmt={fmt}
            format={format}
            image={state.image}
            locale={locale}
            onAlt={setAlt}
            onFormat={setFormat}
            strings={strings}
          />
        ) : null}
      </div>
    </div>
  );
}

function Done({
  image,
  format,
  alt,
  onFormat,
  onAlt,
  strings,
  locale,
  fmt,
}: {
  image: LoadedImage;
  format: SnippetFormat;
  alt: string;
  onFormat: (value: SnippetFormat) => void;
  onAlt: (value: string) => void;
  strings: ImageStrings;
  locale: string;
  fmt: ReturnType<typeof useToolNumber>;
}) {
  const isSvg = image.svgText !== "";
  const active = format === "svgText" && !isSvg ? "dataUri" : format;
  const text = buildSnippet(active, {
    mime: image.mime,
    base64: image.base64,
    svgText: image.svgText,
    alt,
    width: image.width,
    height: image.height,
  });
  const stats = sizeStats(image.bytes, image.mime);
  const dataUri = `data:${image.mime};base64,${image.base64}`;
  const shown = text.length > SHOWN_CHARS ? text.slice(0, SHOWN_CHARS) : text;
  const options = FORMATS.filter((f) => f !== "svgText" || isSvg);
  return (
    <>
      <h2 className={WIDGET_H2}>{strings.resultsHeading}</h2>
      {/* The preview is an img of the file the reader just chose, drawn from its own data URI. */}
      {/* biome-ignore lint/performance/noImgElement: a data URI cannot use next/image */}
      {/* biome-ignore lint/correctness/useImageSize: the natural size is only known once the image has loaded; CSS bounds it */}
      <img
        alt={strings.previewAlt}
        className="max-h-48 max-w-full rounded border border-border bg-card p-2"
        src={dataUri}
      />
      <StatGrid>
        <StatCard emphasis label={strings.typeLabel} value={image.mime} />
        <StatCard
          label={strings.fileSize}
          value={formatSize(image.bytes, locale)}
        />
        <StatCard label={strings.base64Length} value={fmt(stats.base64Chars)} />
        <StatCard
          label={strings.dataUriLength}
          value={fmt(stats.dataUriChars)}
        />
        <StatCard
          label={strings.overhead}
          value={`${fmt(stats.overheadPercent, { maximumFractionDigits: 1 })}%`}
        />
        <StatCard
          label={strings.dimensions}
          value={
            image.width > 0
              ? t(strings.dimensionsValue, {
                  width: fmt(image.width),
                  height: fmt(image.height),
                })
              : strings.unknownDimensions
          }
        />
      </StatGrid>
      {image.width === 0 ? <Notice>{strings.cannotDraw}</Notice> : null}
      <SelectField
        id="itb-format"
        label={strings.formatLabel}
        onChange={onFormat}
        options={options.map((value) => ({
          value,
          label: strings.formats[value],
        }))}
        value={active}
      />
      {active === "html" || active === "markdown" ? (
        <TextField
          id="itb-alt"
          label={strings.altLabel}
          onChange={onAlt}
          value={alt}
        />
      ) : null}
      {active === "html" || active === "markdown" ? (
        <p className={HINT}>{strings.altHint}</p>
      ) : null}
      <div className="space-y-2">
        <label className={LABEL} htmlFor="itb-output">
          {strings.outputLabel}
        </label>
        <textarea
          className={TEXTAREA}
          id="itb-output"
          readOnly
          rows={8}
          spellCheck={false}
          value={shown}
        />
        {text.length > SHOWN_CHARS ? (
          <p className={HINT}>
            {t(strings.truncated, {
              shown: fmt(SHOWN_CHARS),
              total: fmt(text.length),
            })}
          </p>
        ) : null}
      </div>
      <div className="flex flex-wrap gap-3">
        <CopyButton
          copiedLabel={strings.copied}
          label={strings.copy}
          text={text}
        />
        <button
          className={BUTTON_SECONDARY}
          onClick={() => downloadText(strings.filenameText, text)}
          type="button"
        >
          {strings.downloadText}
        </button>
      </div>
      {isSvg ? <Notice>{strings.svgNote}</Notice> : null}
      {stats.dataUriChars > LARGE_URI ? (
        <Notice>
          {t(strings.largeWarning, {
            size: formatSize(stats.dataUriChars, locale),
          })}
        </Notice>
      ) : null}
      <p className={HINT}>{strings.sizeAdvice}</p>
    </>
  );
}
